import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Pagination,
  Skeleton,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { useLanguage } from "../../Language";
import { ResourceView } from "../../../dto/ResourceView";
import {
  LSResultsSelectionProvider,
  useLSResultsSelection,
} from "./LSResultsSelectionContext";
import { LSSearchFilters } from "./ls/LSSearchFilters";
import { useLSSearch, ViewType } from "./ls/LSSearch";
import { LSSearchSortControl } from "./ls/LSSearchSortControl";
import { SaveSearchDialogProvider } from "./ls/SaveSearchDialog";
import { LSResultItem } from "./ls/LSResultItem/LSResultItem";
import { useAddToListDialog } from "./ls/AddToListDialog";
import { LSResultItemSkeleton } from "./ls/LSResultItem/LSResultItemSkeleton";
import { ContentDMOrRSSItem } from "./ls/ContentDMOrRSSItem";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import { SaveSearchButton } from "./ls/SaveSearchButton";
import { LSDownloadableItem } from "./ls/LSDownloadableItem";
import { NoResultsNotice } from "./LSNoResultsNotice";
import {
  isAdvancedSearchTerm,
  isReadingLevelSearch,
} from "./advanced/AdvancedSearchTerm";
import {
  AdvancedSearchTokenDisplay,
  ReadingLevelTokenDisplay,
} from "./advanced/AdvancedSearchTokenDisplay";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";
import { ResultsViewToggle } from "../../ResultsViewToggle";
import {
  GridResultItem,
  GridResultItemSkeleton,
} from "./ls/GridResultItem/GridResultItem";
import { LSResultsAppliedFilters } from "./ls/LSResultsAppliedFilters";
import { useSkipLinks } from "../../../hooks/useSkipLinks";
import SelectionToolBar from "./SelectionToolBar";

export const FILTER_DRAWER_WIDTH = 336;

export const LSResults = () => {
  const {
    searchParams: {
      term,
      rawTerm,
      page,
      facetFiltersKey,
      view,
      pageSize,
      hasFilters,
    },
    setPage,
    totalHits,
    resources,
    loadingResults,
  } = useLSSearch();
  const { translate } = useLanguage();
  const {
    selected,
    selectedLength,
    isAllIndeterminate,
    isAllSelected,
    handleSelectAllChange,
    items,
  } = useLSResultsSelection();
  const isMobile = useIsMobile();
  const showFilterSidebar = useShowFilterSidebar();
  const { showDialog: showAddToListDialog } = useAddToListDialog();

  const defaultListName = useMemo(() => {
    return isAdvancedSearchTerm(rawTerm) || isReadingLevelSearch(rawTerm)
      ? ""
      : term;
  }, [rawTerm, term]);

  const isAdvanced = useMemo(() => {
    return isAdvancedSearchTerm(rawTerm);
  }, [rawTerm]);

  const resultsHeaderRef = React.useRef<HTMLHeadingElement>(null);
  const previousTermRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    // if the search term has not changed, but the facet filters have, scroll to top
    // (facet filters are cleared when the search term changes)
    // scrolling to top on search term and paging changes is handled generically for all dbs by SearchPage
    if (term === previousTermRef.current) {
      resultsHeaderRef.current?.scrollIntoView();
    }
    previousTermRef.current = term;
  }, [facetFiltersKey, term]);

  const isReadingLevel = useMemo(() => {
    return isReadingLevelSearch(rawTerm);
  }, [rawTerm]);

  const gridItemWidth = useGridItemWidth();

  useSkipLinks([
    {
      label: translate("SkipToResults"),
      targetRef: resultsHeaderRef,
      priority: 10,
    },
  ]);

  return (
    <Box>
      <Toolbar
        sx={{
          flexWrap: "wrap",
          gap: 1,
          pl: { sm: 1, xs: 1 },
          pr: { xs: 1, sm: 1 },
        }}
      >
        <Typography
          ref={resultsHeaderRef}
          tabIndex={-1}
          id="ls-results-heading"
          color="inherit"
          variant="h6"
          component="h2"
        >
          {loadingResults ? (
            <Skeleton
              variant="text"
              width={20}
              sx={{ display: "inline-flex" }}
            />
          ) : (
            totalHits
          )}{" "}
          {translate("searchResultsFor")}{" "}
          {!isAdvanced && !isReadingLevel && <span>"{term}"</span>}
        </Typography>
        {isAdvanced && <AdvancedSearchTokenDisplay />}
        {isReadingLevel && <ReadingLevelTokenDisplay />}
        <SaveSearchButton />
      </Toolbar>
      <Collapse in={!showFilterSidebar || hasFilters}>
        <Toolbar sx={{ pl: { sm: 1, xs: 1 }, pr: { xs: 1, sm: 1 } }}>
          <LSResultsAppliedFilters />
        </Toolbar>
      </Collapse>
      {
        <Toolbar
          sx={{
            flexWrap: "wrap",
            flexDirection: isMobile ? "row-reverse" : "row",
            alignItems: "center",
            gap: 1,
            pl: { sm: 0, xs: 0 },
            pr: { xs: 0, sm: 1 },
            ...(selectedLength > 0 && {
              bgcolor: (theme) =>
                alpha(
                  theme.palette.primary.main,
                  theme.palette.action.activatedOpacity,
                ),
            }),
          }}
        >
          {
            <Checkbox
              color="primary"
              disabled={loadingResults || totalHits === 0}
              indeterminate={isAllIndeterminate}
              checked={isAllSelected}
              onChange={handleSelectAllChange}
              slotProps={{
                input: {
                  "aria-label": translate("selectAll"),
                },
              }}
            />
          }
          {selectedLength === 0 && (
            <>
              <Box flex={1}>{/* spacer */}</Box>
              {<LSSearchSortControl />}
              <ResultsViewToggle />
            </>
          )}
          {selectedLength > 0 && (
            <>
              <Typography
                sx={{ flex: "1 1", textWrap: "nowrap" }}
                color="inherit"
                variant="subtitle1"
                component="div"
              >
                {selectedLength} {translate("selected")}
              </Typography>
              <Button
                startIcon={<PlaylistAddIcon />}
                onClick={() =>
                  showAddToListDialog(
                    selected.filter(
                      (id): id is number => typeof id === "number",
                    ),
                    defaultListName,
                  )
                }
              >
                {translate("addToList")}
              </Button>
            </>
          )}
        </Toolbar>
      }
      {view === ViewType.grid && (
        <Box>
          {loadingResults ? (
            <Grid container gap={1}>
              {Array.from({ length: pageSize }).map((_, index) => (
                <GridResultItemSkeleton
                  key={index}
                  sx={{
                    width: gridItemWidth,
                    minWidth: `${GRID_RESULT_MIN_WIDTH}px`,
                  }}
                />
              ))}
            </Grid>
          ) : (
            <>
              <NoResultsNotice />
              {(totalHits ?? 0) > 0 && (
                <Grid container gap={1}>
                  {resources?.map((result) => {
                    return (
                      <GridResultItem
                        key={result.id}
                        resource={result}
                        sx={{
                          width: gridItemWidth,
                          minWidth: `${GRID_RESULT_MIN_WIDTH}px`,
                        }}
                      />
                    );
                  })}
                </Grid>
              )}
            </>
          )}
        </Box>
      )}
      {view === ViewType.list && (
        <Box>
          {loadingResults ? (
            <>
              {Array.from({ length: pageSize }).map((_, index) => (
                <div key={index}>
                  <Divider />
                  <LSResultItemSkeleton />
                </div>
              ))}
            </>
          ) : (
            <>
              <NoResultsNotice />

              {view === ViewType.list &&
                (totalHits ?? 0) > 0 &&
                resources?.map((result) => {
                  const ResultComponent =
                    result.rtype === 1
                      ? result.downloadable
                        ? LSDownloadableItem
                        : LSResultItem
                      : ContentDMOrRSSItem;
                  return (
                    <div key={result.id}>
                      {<Divider />}
                      <ResultComponent resource={result} />
                    </div>
                  );
                })}
            </>
          )}
        </Box>
      )}

      {/* Pagination component is 1 based */}
      {(totalHits ?? 0) > pageSize && (
        <SelectionToolBar
          selected={selected as number[]}
          items={items}
          handleSelectAllChange={handleSelectAllChange}
          selectedFragment={
            <Button
              startIcon={<PlaylistAddIcon />}
              onClick={() =>
                showAddToListDialog(
                  selected.filter((id): id is number => typeof id === "number"),
                  defaultListName,
                )
              }
            >
              {translate("addToList")}
            </Button>
          }
        />
      )}
      <Toolbar>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <Pagination
            page={page}
            count={Math.ceil((totalHits ?? 0) / pageSize)}
            onChange={(_: React.ChangeEvent<unknown>, value: number) =>
              setPage(value)
            }
            sx={{
              justifyContent: "center",
            }}
          />
        </Box>
      </Toolbar>
    </Box>
  );
};

export const FILTER_SIDEBAR_WIDTH = 260;
const GRID_RESULT_MIN_WIDTH = 192;
const GRID_RESULT_GAP = 8; // --mui-spacing
const FILTER_GRID_GAP = 3 * 8; // --mui-spacing
const MIN_GRID_ROW_WITH_FILTERS = 3;

export const useShowFilterSidebar = function () {
  const gridSpaceNeeded = useGridSpaceNeeded(MIN_GRID_ROW_WITH_FILTERS, true);
  const matches = useMediaQuery(`(min-width:${gridSpaceNeeded}px)`);
  return matches;
};

const useGridSpaceNeeded = function (
  minRowCount: number,
  withSidebar: boolean,
) {
  const gutterSize = usePageGutterSize();
  if (withSidebar) {
    return (
      FILTER_SIDEBAR_WIDTH +
      GRID_RESULT_MIN_WIDTH * minRowCount +
      GRID_RESULT_GAP * minRowCount -
      1 +
      FILTER_GRID_GAP +
      gutterSize * 2
    );
  }

  return (
    GRID_RESULT_MIN_WIDTH * minRowCount +
    GRID_RESULT_GAP * minRowCount -
    1 +
    gutterSize * 2
  );
};

const usePageGutterSize = function () {
  return useMediaQuery(`(min-width:600px)`) ? 3 * 8 : 2 * 8; // --mui-spacing
};

const useGridItemWidth = function () {
  const fourPlusSidebar = useMediaQuery(
    `(min-width:${useGridSpaceNeeded(4, true)}px)`,
  );
  const threePlusSidebar = useMediaQuery(
    `(min-width:${useGridSpaceNeeded(3, true)}px)`,
  );
  const fourWithoutSidebar = useMediaQuery(
    `(min-width:${useGridSpaceNeeded(4, false)}px)`,
  );
  const threeWithoutSidebar = useMediaQuery(
    `(min-width:${useGridSpaceNeeded(3, false)}px)`,
  );
  const twoWithoutSidebar = useMediaQuery(
    `(min-width:${useGridSpaceNeeded(2, false)}px)`,
  );

  const width = useMemo(() => {
    if (fourPlusSidebar) {
      return "calc(25% - 8px)";
    }
    if (threePlusSidebar) {
      return "calc(33% - 8px)";
    }
    if (fourWithoutSidebar) {
      return "calc(25% - 8px)";
    }
    if (threeWithoutSidebar) {
      return "calc(33% - 8px)";
    }
    if (twoWithoutSidebar) {
      return "calc(50% - 8px)";
    }
    return "100%";
  }, [
    fourPlusSidebar,
    threePlusSidebar,
    fourWithoutSidebar,
    threeWithoutSidebar,
    twoWithoutSidebar,
  ]);
  return width;
};

export const LSResultsPage: React.FC = () => {
  const getId = useCallback((item: ResourceView | ResourceDWR) => item.id, []);
  const { resources, resourcesKey } = useLSSearch();
  const showFilterSidebar = useShowFilterSidebar();
  const items = useMemo(() => resources || [], [resourcesKey]);
  const { translate } = useLanguage();

  return (
    <Box display="flex" gap={3}>
      {showFilterSidebar && (
        <Box
          flexShrink={0}
          width={FILTER_SIDEBAR_WIDTH}
          flexGrow={0}
          tabIndex={-1}
        >
          <LSSearchFilters />
        </Box>
      )}
      <Box flex={1}>
        <LSResultsSelectionProvider items={items} getId={getId}>
          <SaveSearchDialogProvider>
            <LSResults />
          </SaveSearchDialogProvider>
        </LSResultsSelectionProvider>
      </Box>
    </Box>
  );
};
